import React, { useEffect, useState, useRef, useCallback } from "react";
import Header from "../layouts/Header";
import Footer from "../layouts/Footer";
import { Card, Col, Row, Button, Form } from "react-bootstrap";
import { Link, useParams } from "react-router-dom";
import { getToken } from "../services/authorize";
import axiosInstance from "../services/axiosInterceptor";
import Swal from "sweetalert2";
import { useLoading } from "../contexts/LoadingContext";
import useSchoolSettings from "../hooks/useSchoolSettings";
import {
  calculateGrade, DESIRABLE_LABELS, RTAW_GROUPS, ITEM_MAX, ATT_MARK,
  itemsToScore100, ratingFrom100, passFail, classRank,
  gradeDistribution, ratingDistribution, yearAverage,
  subjectGroupFromCode, GRADE_LEVELS, RATING_LEVELS, THAI_MONTHS_SHORT,
} from "../utils/por5Utils";

const TD_FIELDS = Array.from({ length: 8 }, (_, i) => `td_item${i + 1}`);
const RT_FIELDS = Array.from({ length: 15 }, (_, i) => `rt_item${i + 1}`);

// pagination for wide/long print tables (A4 portrait)
const SESSIONS_PER_PAGE = 15;
const ROWS_PER_PAGE = 27;

const chunk = (arr, size) => {
  const out = [];
  for (let i = 0; i < arr.length; i += size) out.push(arr.slice(i, i + size));
  return out;
};

// ——— print helper (A4 portrait, same approach as StudentPP6.printNode) ———
const printHTML = (html) => {
  const w = window.open("", "_blank");
  w.document.write(`
    <html><head><title>ปพ.5</title>
    <style>
      @page { size: A4; margin: 0; }
      @font-face { font-family: 'THSarabunNew'; src: url('/fonts/THSarabunNew.ttf') format('truetype'); font-weight: 400; font-style: normal; }
      @font-face { font-family: 'THSarabunNew'; src: url('/fonts/THSarabunNew-Bold.ttf') format('truetype'); font-weight: 700; font-style: normal; }
      body { margin: 0; font-family: 'THSarabunNew','TH Sarabun New','Tahoma',Arial,sans-serif; font-size: 14pt; line-height: 1.2; }
      table { border-collapse: collapse; border-spacing: 0; width: 100%; table-layout: auto; font-size: inherit; }
      td, th { border: 1px solid #000 !important; border-width: 1px !important; padding: 1px 2px; vertical-align: middle; word-break: break-word; font-size: inherit; line-height: inherit; }
      .no-border td, .no-border th { border: none; }
      .por5-page, .a4-print { width: 210mm; min-height: 297mm; margin: 0 auto; background: #fff; color: #000; padding: 10mm 10mm 10mm 15mm; box-sizing: border-box; font-family: 'THSarabunNew','TH Sarabun New','Tahoma',Arial,sans-serif; font-size: 14pt; line-height: 1.2; }
      .por5-page, .a4-print { page-break-after: always; break-after: page; overflow: visible; }
      .por5-page:last-child, .a4-print:last-child { page-break-after: auto; break-after: auto; }
      .por5-page *, .a4-print * { font-family: inherit; }
      thead { display: table-header-group; }
      tfoot { display: table-footer-group; }
      tr { page-break-inside: avoid; break-inside: avoid; }
      .no-print { display: none !important; }
    </style>
    </head><body>${html}</body></html>
  `);
  w.document.close();
  w.focus();
  setTimeout(() => { w.print(); w.close(); }, 400);
};

const wrapPage = (inner) => inner.includes('class="por5-page') ? inner : `<div class="por5-page">${inner}</div>`;

// on-screen preview mimics the printed A4 portrait sheet
const SHEET_CSS = `
  @font-face { font-family: 'THSarabunNew'; src: url('/fonts/THSarabunNew.ttf') format('truetype'); font-weight: 400; font-style: normal; }
  @font-face { font-family: 'THSarabunNew'; src: url('/fonts/THSarabunNew-Bold.ttf') format('truetype'); font-weight: 700; font-style: normal; }
  .por5-sheet {
    width: fit-content;
    min-width: 210mm;
    margin: 0 auto;
    color: #000;
    font-family: 'THSarabunNew','TH Sarabun New','Tahoma',Arial,sans-serif;
    font-size: 14pt;
    line-height: 1.2;
  }
  .por5-sheet .por5-page {
    width: 210mm;
    min-height: 297mm;
    margin: 0 auto 16px;
    background: #fff;
    color: #000;
    padding: 10mm 10mm 10mm 15mm;
    box-sizing: border-box;
    box-shadow: 0 1px 6px rgba(0,0,0,.3);
    page-break-after: always;
    break-after: page;
  }
  .por5-sheet table { border-collapse: collapse; border-spacing: 0; width: 100%; font-size: inherit; }
  .por5-sheet td, .por5-sheet th { border: 1px solid #000; border-width: 1px; padding: 1px 2px; vertical-align: middle; word-break: break-word; font-size: 14pt !important; line-height: inherit; }
  .por5-sheet .no-border td, .por5-sheet .no-border th { border: none; }
  .por5-sheet thead { display: table-header-group; }
  .por5-sheet tfoot { display: table-footer-group; }
  .por5-sheet tr { page-break-inside: avoid; break-inside: avoid; }
  .por5-sheet .por5-page:last-child { page-break-after: auto; break-after: auto; margin-bottom: 0; }`;

// ——— shared bits ———

const roomLabel = (ctx) => `มัธยมศึกษาปีที่ ${ctx.tb_class_c_id}/${ctx.r_no}`;

function PageHeader({ title, ctx }) {
  return (
    <div style={{ textAlign: "center", marginBottom: 8 }}>
      <div style={{ fontSize: "16pt", fontWeight: "bold" }}>{title}</div>
      <div style={{ fontSize: "14pt" }}>
        ชั้น {roomLabel(ctx)}&nbsp;&nbsp; วิชา {ctx.sub_subname}&nbsp;&nbsp; รหัส {ctx.sub_subid}&nbsp;&nbsp;
        ภาคเรียนที่ {ctx.sub_semester}&nbsp;&nbsp; ปีการศึกษา {ctx.year_year}
      </div>
    </div>
  );
}

const thin = { fontSize: "14pt", textAlign: "center" };
const nameTd = { fontSize: "14pt", textAlign: "left", whiteSpace: "nowrap" };


function StudentCells({ s, index }) {
  return (
    <>
      <td style={thin}>{index + 1}</td>
      <td style={thin}>{s.st_idnumber}</td>
      <td style={nameTd}>{s.st_name}</td>
    </>
  );
}

const studentNote = (s) => {
  if (s.st_status === "0") return "ลาออก";
  if (s.transfer_room_id) return `ย้ายห้อง ${s.transfer_c_id || ""}/${s.transfer_r_no || ""}`;
  return "";
};

// ——— per-semester derived data ———
const buildView = (sem) => {
  const markMap = new Map();
  sem.scoreMarks.forEach((m) => markMap.set(`${m.study_id}_${m.si_id}`, m.sm_score));
  const absMap = new Map();
  sem.absences.forEach((a) => absMap.set(`${a.study_id}|${a.att_date}|${a.period_id}`, a.att_status));
  const gradeMap = new Map();
  sem.grades.forEach((g) => gradeMap.set(g.study_id, g));
  const tdMap = new Map();
  sem.desirable.forEach((d) => tdMap.set(d.study_id, d));
  const rtMap = new Map();
  sem.rtaw.forEach((r) => rtMap.set(r.study_id, r));

  const desirableScore = (study_id) => {
    const row = tdMap.get(study_id);
    if (!row) return null;
    return itemsToScore100(TD_FIELDS.map((f) => row[f]));
  };
  const rtawScore = (study_id) => {
    const row = rtMap.get(study_id);
    if (!row) return null;
    return itemsToScore100(RT_FIELDS.map((f) => row[f]));
  };

  const activeStudents = sem.students.filter((s) => s.st_status !== "0");
  const rankMap = classRank(
    activeStudents.map((s) => ({
      key: s.study_id,
      total: gradeMap.get(s.study_id)?.g_grade ?? null,
    }))
  );

  return { ...sem, markMap, absMap, gradeMap, tdMap, rtMap, desirableScore, rtawScore, rankMap };
};

// ——— section components (each is one or more printable A4-landscape pages) ———

function SectionCard({ title, sectionKey, registerRef, children }) {
  const ref = useRef();
  useEffect(() => { registerRef(sectionKey, ref); }, [sectionKey, registerRef]);
  return (
    <Card className="card-one mb-3">
      <Card.Header className="d-flex align-items-center justify-content-between">
        <Card.Title as="h6" className="mb-0">{title}</Card.Title>
        <Button variant="white" size="sm" onClick={() => printHTML(wrapPage(ref.current.innerHTML))}>
          <i className="ri-printer-line me-1"></i>พิมพ์ส่วนนี้
        </Button>
      </Card.Header>
      <Card.Body className="p-3" style={{ overflowX: "auto", background: "#e9ecef" }}>
        <div className="por5-sheet">
          <div ref={ref}>{children}</div>
        </div>
      </Card.Body>
    </Card>
  );
}

function CoverSection({ views = [], school }) {
  const view = views[0];
  if (!view) return null;

  const ctx = view.context;
  const hours = (parseFloat(ctx.sub_credit) || 0) * 40;
  const grades = view.students.filter((s) => s.st_status !== "0").map((s) => view.gradeMap.get(s.study_id) || {});
  const dist = gradeDistribution(grades);
  const ratings = view.students.filter((s) => s.st_status !== "0").map((s) => ratingFrom100(view.desirableScore(s.study_id)));
  const rdist = ratingDistribution(ratings);
  return (
    <div className="por5-page" style={{ maxWidth: 900, margin: "0 auto", textAlign: "center", fontSize: "16pt" }}>
      <div style={{ marginBottom: 12 }}>
        {school.logoSrc && <img src={school.logoSrc} alt="logo" style={{ width: 90, height: 90, marginBottom: 6 }} />}
        <div style={{ fontSize: "20pt", fontWeight: "bold" }}>แบบบันทึกผลการเรียนประจำรายวิชา (ปพ.5)</div>
        <div style={{ fontSize: "18pt", fontWeight: "bold" }}>{school.school_name}</div>
        <div>{school.school_address}</div>
        <div>สังกัด{school.school_office}</div>
      </div>

      <table className="no-border" style={{ width: "80%", margin: "0 auto 10px", textAlign: "left" }}>
        <tbody>
          <tr><td style={{ border: "none", width: 180, fontWeight: "bold" }}>ชั้น</td><td style={{ border: "none" }}>{roomLabel(ctx)} ภาคเรียนที่ {ctx.sub_semester} ปีการศึกษา {ctx.year_year}</td></tr>
          <tr><td style={{ border: "none", fontWeight: "bold" }}>กลุ่มสาระการเรียนรู้</td><td style={{ border: "none" }}>{subjectGroupFromCode(ctx.sub_subid)}</td></tr>
          <tr><td style={{ border: "none", fontWeight: "bold" }}>วิชา</td><td style={{ border: "none" }}>{ctx.sub_subname} รหัสวิชา {ctx.sub_subid}</td></tr>
          <tr><td style={{ border: "none", fontWeight: "bold" }}>เวลาเรียน</td><td style={{ border: "none" }}>{hours > 0 ? `${hours} ชั่วโมง` : "-"}</td></tr>
          <tr><td style={{ border: "none", fontWeight: "bold" }}>ครูผู้สอน</td><td style={{ border: "none" }}>{ctx.teacher_name || ""}</td></tr>
          <tr><td style={{ border: "none", fontWeight: "bold" }}>ครูที่ปรึกษา</td><td style={{ border: "none" }}>{ctx.advisor_name || ""}</td></tr>
        </tbody>
      </table>

      <div style={{ marginBottom: 12 }}>
        <div style={{ fontWeight: "bold", textAlign: "left", marginBottom: 4 }}>
          สรุปผลการเรียน ภาคเรียนที่ {ctx.sub_semester}
        </div>
        <table>
          <tbody>
            <tr style={{ fontWeight: "bold", textAlign: "center" }}>
              <td rowSpan={2} style={{ width: 140 }}>จำนวนนักเรียน<br />{dist.total} คน</td>
              <td colSpan={GRADE_LEVELS.length}>จำนวนนักเรียนที่ได้ระดับผลการเรียน</td>
            </tr>
            <tr style={{ fontWeight: "bold", textAlign: "center" }}>
              {GRADE_LEVELS.map((l) => <td key={l}>{l}</td>)}
            </tr>
            <tr style={{ textAlign: "center" }}>
              <td>จำนวน (คน)</td>
              {GRADE_LEVELS.map((l) => <td key={l}>{dist.counts[l]}</td>)}
            </tr>
            <tr style={{ textAlign: "center" }}>
              <td>ร้อยละ</td>
              {GRADE_LEVELS.map((l) => <td key={l}>{dist.percents[l]}</td>)}
            </tr>
          </tbody>
        </table>
        <table style={{ marginTop: 4 }}>
          <tbody>
            <tr style={{ fontWeight: "bold", textAlign: "center" }}>
              <td style={{ width: 140 }}>คุณลักษณะฯ</td>
              {RATING_LEVELS.map((l) => <td key={l}>{l}</td>)}
            </tr>
            <tr style={{ textAlign: "center" }}>
              <td>จำนวน (คน)</td>
              {RATING_LEVELS.map((l) => <td key={l}>{rdist.counts[l]}</td>)}
            </tr>
          </tbody>
        </table>
      </div>

      {/* การอนุมัติผลการเรียน — layout ตามแบบ ปพ.5 ของโรงเรียน */}
      <div style={{ border: "1px solid #000", marginTop: 24, padding: "8px 16px 14px" }}>
        <div style={{ textAlign: "center", fontWeight: "bold", marginBottom: 10 }}>การอนุมัติผลการเรียน</div>
        <div style={{ display: "flex" }}>
          <div style={{ width: 140, textAlign: "left", paddingTop: 52 }}>เรียนเพื่อพิจารณา</div>
          <div style={{ flex: 1, textAlign: "center" }}>
            <div style={{ marginBottom: 14 }}>
              ลงชื่อ &nbsp;.......................................................&nbsp; ครูผู้สอน
            </div>
            <div style={{ marginBottom: 14 }}>
              ลงชื่อ &nbsp;.......................................................&nbsp; หัวหน้ากลุ่มสาระ
            </div>
            <div style={{ marginBottom: 14 }}>
              ลงชื่อ &nbsp;.......................................................&nbsp; หัวหน้าฝ่ายวิชาการ
            </div>
            <div style={{ marginBottom: 14, display: "flex", justifyContent: "center", alignItems: "center", gap: 12 }}>
              <span style={{ display: "inline-block", width: 22, height: 22, border: "1px solid #000" }}></span>
              <span>อนุมัติ</span>
              <span style={{ display: "inline-block", width: 22, height: 22, border: "1px solid #000", marginLeft: 24 }}></span>
              <span>ไม่อนุมัติ</span>
            </div>
            <div>
              ลงชื่อ &nbsp;.......................................................&nbsp; ผู้อำนวยการ
            </div>
          </div>
          <div style={{ width: 140 }}></div>
        </div>
      </div>
    </div>
  );
}

function ScoreRateSection({ views }) {
  const TYPE_LABEL = { normal: "คะแนนเก็บ", midterm: "สอบกลางภาค", final: "สอบปลายภาค" };
  return (
    <div className="por5-page">
      <div style={{ textAlign: "center", fontSize: "16pt", fontWeight: "bold", marginBottom: 8 }}>อัตราการให้คะแนน</div>
      <div style={{ display: "flex", gap: 24, justifyContent: "center" }}>
        {views.map((v) => {
          const groups = { normal: [], midterm: [], final: [] };
          v.scoreItems.forEach((it) => (groups[it.si_type] || groups.normal).push(it));
          const sum = (arr) => arr.reduce((s, it) => s + (parseFloat(it.si_maxscore) || 0), 0);
          return (
            <div key={v.teach_id} style={{ flex: 1, maxWidth: 460 }}>
              <div style={{ fontWeight: "bold", fontSize: "14pt", textAlign: "center", marginBottom: 4 }}>
                ภาคเรียนที่ {v.context.sub_semester} ({v.context.sub_subid})
              </div>
              <table style={{ fontSize: "14pt" }}>
                <tbody>
                  <tr style={{ fontWeight: "bold", textAlign: "center" }}>
                    <td style={{ width: 40 }}>ที่</td><td>รายการ</td>
                    <td style={{ width: 90 }}>ประเภท</td><td style={{ width: 80 }}>คะแนนเต็ม</td>
                  </tr>
                  {v.scoreItems.map((it, i) => (
                    <tr key={it.si_id}>
                      <td style={{ textAlign: "center" }}>{i + 1}</td>
                      <td>{it.si_name}</td>
                      <td style={{ textAlign: "center" }}>{TYPE_LABEL[it.si_type] || TYPE_LABEL.normal}</td>
                      <td style={{ textAlign: "center" }}>{it.si_maxscore}</td>
                    </tr>
                  ))}
                  <tr style={{ fontWeight: "bold" }}>
                    <td colSpan={3} style={{ textAlign: "right" }}>
                      รวม (เก็บ {sum(groups.normal)} / กลางภาค {sum(groups.midterm)} / ปลายภาค {sum(groups.final)})
                    </td>
                    <td style={{ textAlign: "center" }}>{sum(v.scoreItems)}</td>
                  </tr>
                </tbody>
              </table>
              {v.scoreItems.length === 0 && (
                <div style={{ textAlign: "center", color: "#888", fontSize: "14pt", marginTop: 6 }}>ยังไม่ได้ออกแบบตารางคะแนน</div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function StudentListSection({ view }) {
  const studentPages = chunk(view.students, ROWS_PER_PAGE);
  return (
    <div>
      {studentPages.map((students, sp) => (
        <div key={sp} className="por5-page">
          <PageHeader title="บัญชีรายชื่อนักเรียน" ctx={view.context} />
          <table style={{ maxWidth: 700, margin: "0 auto" }}>
            <thead>
              <tr style={{ fontWeight: "bold", textAlign: "center", fontSize: "14pt" }}>
                <td style={{ width: 45 }}>เลขที่</td><td style={{ width: 100 }}>เลขประจำตัว</td>
                <td>ชื่อ - สกุล</td><td style={{ width: 120 }}>สถานะ</td>
              </tr>
            </thead>
            <tbody>
              {students.map((s, i) => (
                <tr key={s.study_id}>
                  <StudentCells s={s} index={sp * ROWS_PER_PAGE + i} />
                  <td style={thin}>{studentNote(s)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ))}
    </div>
  );
}
function AttendanceSection({ view }) {
  const sessionPages = chunk(view.sessions, SESSIONS_PER_PAGE);
  const studentPages = chunk(view.students, ROWS_PER_PAGE);
  if (view.sessions.length === 0) {
    return (
      <div className="por5-page">
        <PageHeader title="เวลาเรียน" ctx={view.context} />
        <div style={{ textAlign: "center", color: "#888" }}>ยังไม่มีข้อมูลการเช็คชื่อ</div>
      </div>
    );
  }
  return (
    <div>
      {studentPages.map((students, sp) =>
        sessionPages.map((sessions, pp) => (
          <div key={`${sp}-${pp}`} className="por5-page">
            <PageHeader title="เวลาเรียน" ctx={view.context} />
            <table>
              <tbody>
                <tr style={{ ...thin, fontWeight: "bold" }}>
                  <td rowSpan={3} style={{ width: 35 }}>เลขที่</td>
                  <td rowSpan={3} style={{ width: 80 }}>เลขประจำตัว</td>
                  <td rowSpan={3} style={{ minWidth: 150 }}>ชื่อ - สกุล</td>
                  <td>เดือน</td>
                  {sessions.map((se, i) => (
                    <td key={i}>{THAI_MONTHS_SHORT[parseInt(se.att_date.slice(5, 7), 10)]}</td>
                  ))}
                  <td rowSpan={3} style={{ width: 35 }}>ขาด</td>
                  <td rowSpan={3} style={{ width: 35 }}>ลา</td>
                  <td rowSpan={3} style={{ width: 35 }}>สาย</td>
                </tr>
                <tr style={{ ...thin, fontWeight: "bold" }}>
                  <td>วันที่</td>
                  {sessions.map((se, i) => <td key={i}>{parseInt(se.att_date.slice(8, 10), 10)}</td>)}
                </tr>
                <tr style={{ ...thin, fontWeight: "bold" }}>
                  <td>ชม.ที่</td>
                  {sessions.map((se, i) => <td key={i}>{pp * SESSIONS_PER_PAGE + i + 1}</td>)}
                </tr>
                {students.map((s, i) => {
                  const totals = { absent: 0, late: 0, leave: 0 };
                  view.sessions.forEach((se) => {
                    const st = view.absMap.get(`${s.study_id}|${se.att_date}|${se.period_id}`);
                    if (st && totals[st] !== undefined) totals[st] += 1;
                  });
                  return (
                    <tr key={s.study_id}>
                      <StudentCells s={s} index={sp * ROWS_PER_PAGE + i} />
                      <td style={thin}></td>
                      {sessions.map((se, j) => {
                        const st = view.absMap.get(`${s.study_id}|${se.att_date}|${se.period_id}`);
                        return <td key={j} style={thin}>{st ? ATT_MARK[st] : ""}</td>;
                      })}
                      <td style={thin}>{totals.absent || ""}</td>
                      <td style={thin}>{totals.leave || ""}</td>
                      <td style={thin}>{totals.late || ""}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            <div style={{ fontSize: "14pt", marginTop: 4 }}>หมายเหตุ: ข = ขาด, ล = ลา, ส = มาสาย (มาเรียน = เว้นว่าง)</div>
          </div>
        ))
      )}
    </div>
  );
}

function IndicatorSection({ view }) {
  const studentPages = chunk(view.students, ROWS_PER_PAGE);
  if (view.scoreItems.length === 0) {
    return (
      <div className="por5-page">
        <PageHeader title="ผลการประเมินตัวชี้วัด / รายการคะแนน" ctx={view.context} />
        <div style={{ textAlign: "center", color: "#888" }}>ยังไม่ได้ออกแบบตารางคะแนน</div>
      </div>
    );
  }
  return (
    <div>
      {studentPages.map((students, sp) => (
        <div key={sp} className="por5-page">
          <PageHeader title="ผลการประเมินตัวชี้วัด / รายการคะแนน" ctx={view.context} />
          <table>
            <tbody>
              <tr style={{ ...thin, fontWeight: "bold" }}>
                <td rowSpan={2} style={{ width: 35 }}>เลขที่</td>
                <td rowSpan={2} style={{ width: 80 }}>เลขประจำตัว</td>
                <td rowSpan={2} style={{ minWidth: 150 }}>ชื่อ - สกุล</td>
                {view.scoreItems.map((it) => (
                  <td key={it.si_id} colSpan={2}>{it.si_name}</td>
                ))}
                <td rowSpan={2} style={{ width: 50 }}>รวม<br />(100)</td>
                <td rowSpan={2} style={{ width: 55 }}>เกรด</td>
              </tr>
              <tr style={{ ...thin, fontWeight: "bold" }}>
                {view.scoreItems.map((it) => (
                  <React.Fragment key={it.si_id}>
                    <td style={{ width: 45 }}>{it.si_maxscore}</td>
                    <td style={{ width: 40 }}>ผล</td>
                  </React.Fragment>
                ))}
              </tr>
              {students.map((s, i) => {
                const g = view.gradeMap.get(s.study_id);
                const waiting = Number(g?.g_waiting) === 1;
                return (
                  <tr key={s.study_id}>
                    <StudentCells s={s} index={sp * ROWS_PER_PAGE + i} />
                    {view.scoreItems.map((it) => {
                      const mark = view.markMap.get(`${s.study_id}_${it.si_id}`);
                      return (
                        <React.Fragment key={it.si_id}>
                          <td style={thin}>{mark ?? ""}</td>
                          <td style={thin}>{passFail(mark, it.si_maxscore) ?? ""}</td>
                        </React.Fragment>
                      );
                    })}
                    <td style={{ ...thin, fontWeight: "bold" }}>{waiting ? "ร" : g?.g_grade ?? ""}</td>
                    <td style={{ ...thin, fontWeight: "bold" }}>{waiting ? "ร" : calculateGrade(g?.g_grade) ?? ""}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ))}
    </div>
  );
}

function ItemAssessmentSection({ view, title, fields, headerGroups, labels, getRow }) {
  const studentPages = chunk(view.students, ROWS_PER_PAGE);
  return (
    <div>
      {studentPages.map((students, sp) => (
        <div key={sp} className="por5-page">
          <PageHeader title={title} ctx={view.context} />
          <table>
            <tbody>
              {headerGroups && (
                <tr style={{ ...thin, fontSize: "12pt", fontWeight: "bold" }}>
                  <td colSpan={3}></td>
                  {headerGroups.map((g) => <td key={g.name} colSpan={g.count}>{g.name}</td>)}
                  <td colSpan={3}></td>
                </tr>
              )}
              <tr style={{ ...thin, fontSize: "12pt", fontWeight: "bold" }}>
                <td style={{ width: 35 }}>เลขที่</td>
                <td style={{ width: 80 }}>เลขประจำตัว</td>
                <td style={{ minWidth: 150 }}>ชื่อ - สกุล</td>
                {fields.map((f, i) => (
                  <td key={f} style={{ width: 40 }} title={labels[i]}>
                    {headerGroups ? (i % 5) + 1 : i + 1}
                  </td>
                ))}
                <td style={{ width: 45 }}>รวม</td>
                <td style={{ width: 50 }}>เต็ม 100</td>
                <td style={{ width: 80 }}>ผลประเมิน</td>
              </tr>
              {!headerGroups && (
                <tr style={{ ...thin, fontSize: "12pt" }}>
                  <td colSpan={3} style={{ textAlign: "center", fontWeight: "bold" }}>รายการ</td>
                  {labels.map((l, i) => <td key={i} style={{ lineHeight: 1.1 }}>{l}</td>)}
                  <td colSpan={3}></td>
                </tr>
              )}
              {students.map((s, i) => {
                const row = getRow(s.study_id) || {};
                const values = fields.map((f) => row[f]);
                const score100 = itemsToScore100(values);
                return (
                  <tr key={s.study_id}>
                    <StudentCells s={s} index={sp * ROWS_PER_PAGE + i} />
                    {fields.map((f) => <td key={f} style={thin}>{row[f] ?? ""}</td>)}
                    <td style={thin}>
                      {values.some((v) => v != null)
                        ? values.reduce((sum, v) => sum + (parseFloat(v) || 0), 0)
                        : ""}
                    </td>
                    <td style={{ ...thin, fontWeight: "bold" }}>{score100 ?? ""}</td>
                    <td style={thin}>{ratingFrom100(score100) ?? ""}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          <div style={{ fontSize: "14pt", marginTop: 4 }}>
            คะแนน 0-{ITEM_MAX} ต่อข้อ | เกณฑ์: ≥85 ดีเยี่ยม, ≥70 ดี, ≥50 ผ่านเกณฑ์, &lt;50 ไม่ผ่านเกณฑ์
          </div>
        </div>
      ))}
    </div>
  );
}

function SummarySection({ view }) {
  const studentPages = chunk(view.students, ROWS_PER_PAGE);
  return (
    <div>
      {studentPages.map((students, sp) => (
        <div key={sp} className="por5-page">
          <PageHeader title={`สรุปผลการเรียนและผลการประเมิน ภาคเรียนที่ ${view.context.sub_semester}`} ctx={view.context} />
          <table style={{ maxWidth: 950, margin: "0 auto" }}>
            <tbody>
              <tr style={{ ...thin, fontWeight: "bold" }}>
                <td rowSpan={2} style={{ width: 35 }}>เลขที่</td>
                <td rowSpan={2} style={{ width: 80 }}>เลขประจำตัว</td>
                <td rowSpan={2} style={{ minWidth: 150 }}>ชื่อ - สกุล</td>
                <td colSpan={2}>ผลการเรียน</td>
                <td rowSpan={2} style={{ width: 55 }}>ลำดับที่</td>
                <td colSpan={2}>คุณลักษณะอันพึงประสงค์</td>
                <td colSpan={2}>อ่าน คิดวิเคราะห์ เขียน</td>
                <td rowSpan={2} style={{ width: 90 }}>หมายเหตุ</td>
              </tr>
              <tr style={{ ...thin, fontWeight: "bold" }}>
                <td style={{ width: 60 }}>คะแนน (100)</td>
                <td style={{ width: 60 }}>ระดับผลการเรียน</td>
                <td style={{ width: 60 }}>คะแนน (100)</td>
                <td style={{ width: 80 }}>ผลประเมิน</td>
                <td style={{ width: 60 }}>คะแนน (100)</td>
                <td style={{ width: 80 }}>ผลประเมิน</td>
              </tr>
              {students.map((s, i) => {
                const g = view.gradeMap.get(s.study_id);
                const waiting = Number(g?.g_waiting) === 1;
                const ds = view.desirableScore(s.study_id);
                const rs = view.rtawScore(s.study_id);
                return (
                  <tr key={s.study_id}>
                    <StudentCells s={s} index={sp * ROWS_PER_PAGE + i} />
                    <td style={thin}>{waiting ? "ร" : g?.g_grade ?? ""}</td>
                    <td style={{ ...thin, fontWeight: "bold" }}>{waiting ? "ร" : calculateGrade(g?.g_grade) ?? ""}</td>
                    <td style={thin}>{view.rankMap.get(s.study_id) ?? ""}</td>
                    <td style={thin}>{ds ?? ""}</td>
                    <td style={thin}>{ratingFrom100(ds) ?? ""}</td>
                    <td style={thin}>{rs ?? ""}</td>
                    <td style={thin}>{ratingFrom100(rs) ?? ""}</td>
                    <td style={thin}>{studentNote(s)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ))}
    </div>
  );
}

function YearSummarySection({ views }) {
  const [v1, v2] = views;
  const ctx = v1.context;
  // union of students across both semesters, keyed by st_id
  const byStId = new Map();
  [v1, v2].forEach((v) => v.students.forEach((s) => { if (!byStId.has(s.st_id)) byStId.set(s.st_id, s); }));
  const students = [...byStId.values()].sort((a, b) => String(a.st_idnumber).localeCompare(String(b.st_idnumber)));

  const findStudy = (v, st_id) => v.students.find((s) => s.st_id === st_id)?.study_id;

  const rows = students.map((s) => {
    const sid1 = findStudy(v1, s.st_id);
    const sid2 = findStudy(v2, s.st_id);
    const g1 = sid1 != null ? v1.gradeMap.get(sid1) : null;
    const g2 = sid2 != null ? v2.gradeMap.get(sid2) : null;
    const waiting = Number(g1?.g_waiting) === 1 || Number(g2?.g_waiting) === 1;
    const total = waiting ? null : yearAverage(g1?.g_grade ?? null, g2?.g_grade ?? null);
    const ds = yearAverage(sid1 != null ? v1.desirableScore(sid1) : null, sid2 != null ? v2.desirableScore(sid2) : null);
    const rs = yearAverage(sid1 != null ? v1.rtawScore(sid1) : null, sid2 != null ? v2.rtawScore(sid2) : null);
    return { s, waiting, total, ds, rs };
  });

  const rankMap = classRank(
    rows.filter((r) => r.s.st_status !== "0").map((r) => ({ key: r.s.st_id, total: r.total }))
  );

  const pages = chunk(rows, ROWS_PER_PAGE);
  return (
    <div>
      {pages.map((pageRows, sp) => (
        <div key={sp} className="por5-page">
          <div style={{ textAlign: "center", marginBottom: 8 }}>
            <div style={{ fontSize: "16pt", fontWeight: "bold" }}>สรุปผลการเรียนและผลการประเมิน ตลอดปีการศึกษา {ctx.year_year}</div>
            <div style={{ fontSize: "14pt" }}>
              ชั้น {roomLabel(ctx)}&nbsp;&nbsp; วิชา {v1.context.sub_subname} ({v1.context.sub_subid} / {v2.context.sub_subid})
            </div>
          </div>
          <table style={{ maxWidth: 1000, margin: "0 auto" }}>
            <tbody>
              <tr style={{ ...thin, fontWeight: "bold" }}>
                <td rowSpan={2} style={{ width: 35 }}>เลขที่</td>
                <td rowSpan={2} style={{ width: 80 }}>เลขประจำตัว</td>
                <td rowSpan={2} style={{ minWidth: 150 }}>ชื่อ - สกุล</td>
                <td colSpan={2}>ภาคเรียนที่ 1</td>
                <td colSpan={2}>ภาคเรียนที่ 2</td>
                <td colSpan={2}>ตลอดปี</td>
                <td rowSpan={2} style={{ width: 50 }}>ลำดับที่</td>
                <td colSpan={2}>คุณลักษณะฯ (ปี)</td>
                <td colSpan={2}>อ่าน คิดฯ (ปี)</td>
              </tr>
              <tr style={{ ...thin, fontWeight: "bold" }}>
                <td style={{ width: 55 }}>คะแนน</td><td style={{ width: 50 }}>เกรด</td>
                <td style={{ width: 55 }}>คะแนน</td><td style={{ width: 50 }}>เกรด</td>
                <td style={{ width: 55 }}>คะแนน</td><td style={{ width: 50 }}>เกรด</td>
                <td style={{ width: 55 }}>คะแนน</td><td style={{ width: 75 }}>ผลประเมิน</td>
                <td style={{ width: 55 }}>คะแนน</td><td style={{ width: 75 }}>ผลประเมิน</td>
              </tr>
              {pageRows.map((r, i) => {
                const sid1 = findStudy(v1, r.s.st_id);
                const sid2 = findStudy(v2, r.s.st_id);
                const g1 = sid1 != null ? v1.gradeMap.get(sid1) : null;
                const g2 = sid2 != null ? v2.gradeMap.get(sid2) : null;
                return (
                  <tr key={r.s.st_id}>
                    <StudentCells s={r.s} index={sp * ROWS_PER_PAGE + i} />
                    <td style={thin}>{Number(g1?.g_waiting) === 1 ? "ร" : g1?.g_grade ?? ""}</td>
                    <td style={thin}>{Number(g1?.g_waiting) === 1 ? "ร" : calculateGrade(g1?.g_grade) ?? ""}</td>
                    <td style={thin}>{Number(g2?.g_waiting) === 1 ? "ร" : g2?.g_grade ?? ""}</td>
                    <td style={thin}>{Number(g2?.g_waiting) === 1 ? "ร" : calculateGrade(g2?.g_grade) ?? ""}</td>
                    <td style={{ ...thin, fontWeight: "bold" }}>{r.waiting ? "ร" : r.total ?? ""}</td>
                    <td style={{ ...thin, fontWeight: "bold" }}>{r.waiting ? "ร" : calculateGrade(r.total) ?? ""}</td>
                    <td style={thin}>{rankMap.get(r.s.st_id) ?? ""}</td>
                    <td style={thin}>{r.ds ?? ""}</td>
                    <td style={thin}>{ratingFrom100(r.ds) ?? ""}</td>
                    <td style={thin}>{r.rs ?? ""}</td>
                    <td style={thin}>{ratingFrom100(r.rs) ?? ""}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ))}
    </div>
  );
}

// ——— main page ———

export default function Por5Book() {
  const { teach_id } = useParams();
  const { setLoading } = useLoading();
  const school = useSchoolSettings();
  const [, setSkin] = useState(localStorage.getItem("skin-mode") ? "dark" : "");

  const [pairId, setPairId] = useState("");
  const [documentSemester, setDocumentSemester] = useState("");
  const [pairLoaded, setPairLoaded] = useState(false);
  const [book, setBook] = useState(null);

  const sectionRefs = useRef({});
  const registerRef = useCallback((key, ref) => { sectionRefs.current[key] = ref; }, []);

  // load pairing suggestion once
  useEffect(() => {
    axiosInstance
      .get(`${process.env.REACT_APP_API}/por5/pairCandidates/${teach_id}`, {
        headers: { Authorization: `Bearer ${getToken()}` },
      })
      .then((res) => {
        setPairId(res.data.auto ? String(res.data.auto) : "");
      })
      .catch(() => {})
      .finally(() => setPairLoaded(true));
  }, [teach_id]);

  // load book whenever pairing changes
  useEffect(() => {
    if (!pairLoaded) return;
    setLoading(true);
    const q = pairId ? `?pair_teach_id=${pairId}` : "";
    axiosInstance
      .get(`${process.env.REACT_APP_API}/por5/book/${teach_id}${q}`, {
        headers: { Authorization: `Bearer ${getToken()}` },
      })
      .then((res) => setBook(res.data))
      .catch((err) => {
        Swal.fire({ icon: "error", title: "เกิดข้อผิดพลาด", text: err.response?.data?.message || err.message });
        setBook(null);
      })
      .finally(() => setLoading(false));
  }, [teach_id, pairId, pairLoaded, setLoading]);

  const views = (book?.semesters || []).map(buildView);
  const semesterOptions = Array.from(new Set(views.map((v) => String(v.context.sub_semester))));
  const selectedSemester = semesterOptions.includes(documentSemester) ? documentSemester : semesterOptions[0] || "";
  const documentViews = selectedSemester
    ? views.filter((v) => String(v.context.sub_semester) === selectedSemester)
    : views;
  const ctx = documentViews[0]?.context || views[0]?.context;

  const printAll = () => {
    const order = Object.keys(sectionRefs.current);
    const html = order
      .map((k) => sectionRefs.current[k]?.current?.innerHTML)
      .filter(Boolean)
      .map(wrapPage)
      .join("");
    printHTML(html);
  };

  return (
    <React.Fragment>
      <Header onSkin={setSkin} />
      <style>{SHEET_CSS}</style>
      <div className="main main-app p-3 p-lg-4">
        <div className="d-sm-flex align-items-center justify-content-between mb-4">
          <div>
            <ol className="breadcrumb fs-sm mb-1">
              <li className="breadcrumb-item"><Link to="/teaching/saveGrade">การสอน</Link></li>
              <li className="breadcrumb-item active">เล่ม ปพ.5</li>
            </ol>
            <h4 className="main-title mb-0">เล่ม ปพ.5 — แบบบันทึกผลการเรียนประจำรายวิชา</h4>
            {ctx && (
              <p className="text-muted mb-0 fs-sm">
                {ctx.sub_subid} {ctx.sub_subname} ห้อง {ctx.tb_class_c_id}/{ctx.r_no} ปีการศึกษา {ctx.year_year}
              </p>
            )}
          </div>
          <div className="d-flex align-items-center gap-3 mt-3 mt-md-0">
            <div className="d-flex align-items-center gap-2">
              {semesterOptions.map((sem) => (
                <Form.Check
                  key={sem}
                  inline
                  type="radio"
                  id={`por5-semester-${sem}`}
                  name="por5-semester-filter"
                  label={`เทอม ${sem}`}
                  checked={selectedSemester === sem}
                  onChange={() => setDocumentSemester(sem)}
                />
              ))}
            </div>
            <Button variant="primary" size="sm" onClick={printAll} disabled={documentViews.length === 0}>
              <i className="ri-printer-line me-1"></i>พิมพ์ทั้งเล่ม
            </Button>
          </div>
        </div>

        {documentViews.length > 0 && (
          <>
            <SectionCard title="1. ปก" sectionKey="cover" registerRef={registerRef}>
              <CoverSection views={documentViews} school={school} />
            </SectionCard>

            <SectionCard title="2. อัตราการให้คะแนน" sectionKey="scorerate" registerRef={registerRef}>
              <ScoreRateSection views={documentViews} />
            </SectionCard>

            {documentViews.map((v) => (
              <React.Fragment key={v.teach_id}>
                <SectionCard title={`3. บัญชีรายชื่อนักเรียน (เทอม ${v.context.sub_semester})`} sectionKey={`list-${v.teach_id}`} registerRef={registerRef}>
                  <StudentListSection view={v} />
                </SectionCard>
                <SectionCard title={`4. เวลาเรียน (เทอม ${v.context.sub_semester})`} sectionKey={`att-${v.teach_id}`} registerRef={registerRef}>
                  <AttendanceSection view={v} />
                </SectionCard>
                <SectionCard title={`5. ผลการประเมินตัวชี้วัด (เทอม ${v.context.sub_semester})`} sectionKey={`ind-${v.teach_id}`} registerRef={registerRef}>
                  <IndicatorSection view={v} />
                </SectionCard>
                <SectionCard title={`6. คุณลักษณะอันพึงประสงค์ (เทอม ${v.context.sub_semester})`} sectionKey={`des-${v.teach_id}`} registerRef={registerRef}>
                  <ItemAssessmentSection
                    view={v}
                    title={`ผลการประเมินคุณลักษณะอันพึงประสงค์ ภาคเรียนที่ ${v.context.sub_semester}`}
                    fields={TD_FIELDS}
                    labels={DESIRABLE_LABELS}
                    getRow={(id) => v.tdMap.get(id)}
                  />
                </SectionCard>
                <SectionCard title={`7. อ่าน คิดวิเคราะห์ เขียน (เทอม ${v.context.sub_semester})`} sectionKey={`rtaw-${v.teach_id}`} registerRef={registerRef}>
                  <ItemAssessmentSection
                    view={v}
                    title={`ผลการประเมินอ่าน คิดวิเคราะห์ และเขียนสื่อความหมาย ภาคเรียนที่ ${v.context.sub_semester}`}
                    fields={RT_FIELDS}
                    headerGroups={RTAW_GROUPS}
                    labels={RT_FIELDS.map((_, i) => `${RTAW_GROUPS[Math.floor(i / 5)].name} ${(i % 5) + 1}`)}
                    getRow={(id) => v.rtMap.get(id)}
                  />
                </SectionCard>
                <SectionCard title={`8. สรุปผลการเรียน (เทอม ${v.context.sub_semester})`} sectionKey={`sum-${v.teach_id}`} registerRef={registerRef}>
                  <SummarySection view={v} />
                </SectionCard>
              </React.Fragment>
            ))}

            {documentViews.length === 2 && (
              <SectionCard title="9. สรุปผลตลอดปีการศึกษา" sectionKey="yearsum" registerRef={registerRef}>
                <YearSummarySection views={documentViews} />
              </SectionCard>
            )}
          </>
        )}

        <Row className="g-3">
          <Col>
            <Card className="card-one">
              <Card.Body className="p-3">
                <div className="text-muted fs-sm">
                  <i className="ri-information-line me-1"></i>
                  ข้อมูลดึงอัตโนมัติจากระบบ: การเช็คชื่อ, ตารางคะแนน (score item/mark), ผลการเรียน
                  และผลประเมินคุณลักษณะฯ / อ่านคิดวิเคราะห์ ที่กรอกจากหน้า
                  <Link to={`/teaching/por5-assessment/${teach_id}`} className="ms-1">ประเมิน ปพ.5</Link>
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>
        <Footer />
      </div>
    </React.Fragment>
  );
}
